import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useTransportQuery } from "@/transport"

import { getMetadataFilterValidationError } from "@/lib/metadataFilters"
import type { TransportOperationResponse } from "@/types_api/backend/transport/operations"
import type { UploadedConversationKeysetRequest } from "@/types_api/backend/transport/uploaded_conversation_keyset_request"
import type {
  OutcomeMetricInsightResource,
  OutcomeWindowResource,
} from "@/types_api/backend/transport/uploaded_conversation_keyset_response"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import { normalizeConversationTransportRow } from "@/utils/conversationsTransportRows"

import type {
  ConversationsOutcomeInsights,
  ConversationsTransportFilters,
} from "../types"

const CONVERSATIONS_SUMMARY_SAMPLE_LIMIT = 100

function mapWindow(
  window: OutcomeWindowResource,
): ConversationsOutcomeInsights["current"] {
  return {
    totalItems: window.total_items,
    statusKnownItems: window.status_known_items,
    completedItems: window.completed_items,
    inProgressItems: window.in_progress_items,
    failedItems: window.failed_items,
    unscorableItems: window.unscorable_items,
  }
}

function mapOutcomeInsights(
  response: TransportOperationResponse<"queryUploadedConversationKeyset">,
): ConversationsOutcomeInsights | undefined {
  const insights = response.outcome_insights
  if (!insights) {
    return undefined
  }

  const mapMetric = (metric: OutcomeMetricInsightResource) => ({
    metricId: metric.metric_id,
    isBinary: metric.is_binary,
    currentCount: metric.current_count,
    currentAverage: metric.current_average ?? null,
    previousCount: metric.previous_count,
    previousAverage: metric.previous_average ?? null,
  })

  return {
    windowDays: insights.window_days,
    current: mapWindow(insights.current),
    previous: mapWindow(insights.previous),
    metrics: (insights.metrics ?? []).map(mapMetric),
    agentMetrics: (insights.segment_metrics ?? []).map((metric) => ({
      ...mapMetric(metric),
      agentId: metric.segment_id,
    })),
  }
}

export function useConversationsSummaryRowsTransport({
  analysisWindowDays,
  enabled = true,
  filters,
  refetchInterval,
  simulationOutputIds,
}: {
  /** Window the current/prior comparison covers; omit to let the backend pick. */
  analysisWindowDays?: 1 | 7 | 30
  enabled?: boolean
  filters: ConversationsTransportFilters
  refetchInterval?: number | false
  /** Restricts the sample to these conversations; undefined applies no restriction. */
  simulationOutputIds?: string[]
}) {
  const validationError = getMetadataFilterValidationError(
    filters.metadata_filters,
  )
  const request = useMemo(
    () =>
      ({
        limit: CONVERSATIONS_SUMMARY_SAMPLE_LIMIT,
        analysis_window_days: analysisWindowDays,
        filters,
        simulation_output_ids: simulationOutputIds,
      }) satisfies UploadedConversationKeysetRequest,
    [analysisWindowDays, filters, simulationOutputIds],
  )

  const query = useTransportQuery({
    operation: "queryUploadedConversationKeyset",
    request: validationError ? null : request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled: enabled && !validationError,
      abortOnUnmount: true,
      refetchInterval,
      refetchOnWindowFocus: false,
      retry: false,
      select: (rawResponse) => ({
        rows: rawResponse.items.map((row) =>
          normalizeConversationTransportRow(row),
        ),
        outcomeInsights: mapOutcomeInsights(rawResponse),
      }),
    },
  })

  return {
    data: (validationError
      ? []
      : (query.data?.rows ?? [])) as ConversationsTableResult[],
    outcomeInsights: validationError ? undefined : query.data?.outcomeInsights,
    error: validationError ? new Error(validationError) : query.error,
    hasData: !validationError && query.data !== undefined,
    isError: Boolean(validationError) || query.isError,
    isFetching: query.isFetching,
    isLoading: !validationError && query.isLoading,
    refetch: query.refetch,
  }
}
